import i18n from 'app/services/i18n';

import { joinStringsWithConjunction } from './string';

// Local Typings
export interface StrengthGuideItem {
  isValid: boolean;
  message: string;
}
interface StrengthGuideBase {
  hasNumber: StrengthGuideItem;
  isMinLength: StrengthGuideItem;
}
interface StrengthGuideNormal {
  hasLetter: StrengthGuideItem;
  hasLower?: never;
  hasSymbol?: never;
  hasUpper?: never;
}
interface StrengthGuideStrong {
  hasLetter?: never;
  hasLower: StrengthGuideItem;
  hasSymbol: StrengthGuideItem;
  hasUpper: StrengthGuideItem;
}
export type StrengthGuide = StrengthGuideBase & (StrengthGuideNormal | StrengthGuideStrong);

interface IsPasswordValidResult {
  errorMessage: null | string;
  isValid: boolean;
  strengthGuide?: StrengthGuide;
}

export const validatePostalCode = (str: string, isRequired: boolean, isUsOrCa: boolean) => {
  if (isRequired && !str.length) {
    return [i18n.t('common:required', 'Required')];
  }
  if (isUsOrCa && !/^(\d{5}(-\d{4})?|[A-CEGHJ-NPRSTVXY]\d[A-CEGHJ-NPRSTV-Z] ?\d[A-CEGHJ-NPRSTV-Z]\d)?$/.test(str)) {
    return [i18n.t('util:validators.invalidPostalCode', 'Must be a valid postal code')];
  }

  return null;
};

export function isPasswordValid(
  pwd: string,
  requiresExtraStrength = false,
  withStrengthGuide = false,
): IsPasswordValidResult {
  const validationMessagePredicates = {
    hasLetter: i18n.t('util:validator.password.letterRequirement', 'At least one letter'),
    hasLower: i18n.t('util:validator.password.lowercaseRequirement', 'A lowercase letter'),
    hasNumber: i18n.t('util:validator.password.numberRequirement', 'At least one number'),
    hasSymbol: i18n.t('util:validator.password.symbolRequirement', 'A special character'),
    hasUpper: i18n.t('util:validator.password.uppercaseRequirement', 'An uppercase letter'),
  };

  const minLength = 8;
  const minLengthValidationMessagePredicate = i18n.t('util:validators.password.minLength', {
    defaultValue: '{{minLength}} characters minimum',
    minLength,
  });
  const letter = /[a-zA-Z]/;
  const lowerCase = /[a-z]/;
  const upperCase = /[A-Z]/;
  const number = /[0-9]/;
  const nonDigit = /[\D]/;
  const nonLetter = /[\W]/;
  const hasLetter = letter.test(pwd);
  const hasLower = lowerCase.test(pwd);
  const hasUpper = upperCase.test(pwd);
  const hasNumber = number.test(pwd);
  const hasSymbol = nonDigit.test(pwd) && nonLetter.test(pwd);
  const isMinLength = pwd.trim().length >= minLength;

  const strengthGuide: StrengthGuide = {
    hasNumber: {
      isValid: hasNumber,
      message: validationMessagePredicates.hasNumber,
    },
    isMinLength: {
      isValid: isMinLength,
      message: minLengthValidationMessagePredicate,
    },
    ...(requiresExtraStrength
      ? ({
        hasLower: {
          isValid: hasLower,
          message: validationMessagePredicates.hasLower,
        },
        hasSymbol: {
          isValid: hasSymbol,
          message: validationMessagePredicates.hasSymbol,
        },
        hasUpper: {
          isValid: hasUpper,
          message: validationMessagePredicates.hasUpper,
        },
      })
      : ({
        hasLetter: {
          isValid: hasLetter,
          message: validationMessagePredicates.hasLetter,
        },
      })
    ),
  };

  const validationStrings: string[] = [];

  if (!hasNumber) {
    validationStrings.push(validationMessagePredicates.hasNumber);
  }
  if (requiresExtraStrength) {
    if (!hasLower) {
      validationStrings.push(validationMessagePredicates.hasLower);
    }
    if (!hasUpper) {
      validationStrings.push(validationMessagePredicates.hasUpper);
    }
    if (!hasSymbol) {
      validationStrings.push(validationMessagePredicates.hasSymbol);
    }
  } else {
    if (!hasLetter) {
      validationStrings.push(validationMessagePredicates.hasLetter);
    }
  }
  if (!isMinLength) {
    validationStrings.push(minLengthValidationMessagePredicate);
  }

  const errorMessage = validationStrings.length > 0
    ? i18n.t('util:validators.errorMessage', {
      defaultValue: 'Must contain {{validationStrings}}',
      validationStrings: joinStringsWithConjunction(validationStrings),
    })
    : null;

  return {
    errorMessage: withStrengthGuide ? null : errorMessage,
    isValid: validationStrings.length === 0,
    strengthGuide: withStrengthGuide ? strengthGuide : undefined,
  };
}
